import React, { useEffect, useCallback, useState } from 'react';

import { useToasts } from 'react-toast-notifications';
import { Link, useParams, useHistory } from 'react-router-dom';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';

// core components
import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import TextField from '@material-ui/core/TextField';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';

import api from '../../services/api';

const useStyles = makeStyles(styles);
const EMPTY_PRODUCT_COMMENT = 'Sem comentário para este produto.';

const getFirstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const formatDate = dateValue => {
  if (!dateValue) {
    return '-';
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleDateString('pt-br', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const parseIntegerInRange = (value, min, max) => {
  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return null;
  }

  if (numeric < min || numeric > max) {
    return null;
  }

  return Math.round(numeric);
};

const renderStars = value => {
  const score = parseIntegerInRange(value, 1, 5);

  if (!score) {
    return <Typography variant="body2">-</Typography>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {Array.from({ length: 5 }).map((_, index) =>
        index < score ? (
          <Star
            key={`star-${index}`}
            style={{ fontSize: 20, color: '#f9a825' }}
          />
        ) : (
          <StarBorder
            key={`star-${index}`}
            style={{ fontSize: 20, color: '#c7c7c7' }}
          />
        ),
      )}
      <span style={{ marginLeft: 6, fontWeight: 700 }}>{score}/5</span>
    </div>
  );
};

const renderNps = value => {
  const score = parseIntegerInRange(value, 0, 10);

  if (score === null) {
    return <Chip label="NPS: -" size="small" />;
  }

  if (score >= 9) {
    return (
      <Chip
        label={`NPS: ${score}/10`}
        size="small"
        style={{ backgroundColor: '#2e7d32', color: '#fff' }}
      />
    );
  }

  if (score >= 7) {
    return (
      <Chip
        label={`NPS: ${score}/10`}
        size="small"
        style={{ backgroundColor: '#ef6c00', color: '#fff' }}
      />
    );
  }

  return (
    <Chip
      label={`NPS: ${score}/10`}
      size="small"
      style={{ backgroundColor: '#c62828', color: '#fff' }}
    />
  );
};

const normalizeRatingDetails = rating => {
  const customer = getFirstValue(rating.customer, rating.user, {}) || {};
  const order = getFirstValue(rating.order, rating.pedido, {}) || {};
  const itemRatings = Array.isArray(rating.itemRatings)
    ? rating.itemRatings
    : Array.isArray(rating.item_ratings)
    ? rating.item_ratings
    : [];

  const items = itemRatings.map((itemRating, index) => {
    const product = getFirstValue(itemRating.product, {}) || {};

    return {
      key: getFirstValue(itemRating.id, `item-${index}`),
      productName: getFirstValue(product.nome, product.name, '-'),
      productCode: getFirstValue(product.codigo, product.code, '-'),
      productScore: getFirstValue(
        itemRating.product_score,
        itemRating.productScore,
        '-',
      ),
      comment: getFirstValue(
        itemRating.comment,
        itemRating.comentario,
        itemRating.product_comment,
        itemRating.productComment,
        itemRating.review,
        itemRating.feedback,
        itemRating.observacao,
        itemRating.obs,
        itemRating.description,
        itemRating.rating?.comment,
        EMPTY_PRODUCT_COMMENT,
      ),
    };
  });

  const deliveredOnTime = getFirstValue(
    rating.delivered_on_time,
    rating.deliveredOnTime,
    null,
  );
  const wouldBuyAgain = getFirstValue(
    rating.would_buy_again,
    rating.wouldBuyAgain,
    null,
  );

  return {
    id: getFirstValue(rating.id, rating._id),
    customerName: getFirstValue(customer.name, rating.customer_name, '-'),
    customerEmail: getFirstValue(customer.email, rating.customer_email, '-'),
    customerPhone1: getFirstValue(
      customer.tel_01,
      customer.phone,
      rating.customer_phone,
      '-',
    ),
    customerPhone2: getFirstValue(customer.tel_02, customer.phone_2, '-'),
    customerPhone3: getFirstValue(customer.tel_03, customer.phone_3, '-'),
    deliveryScore: getFirstValue(
      rating.delivery_score,
      rating.deliveryScore,
      '-',
    ),
    npsScore: getFirstValue(rating.nps_score, rating.npsScore, '-'),
    orderId: getFirstValue(rating.order_id, order.id, order.order_id, '-'),
    createdAt: formatDate(
      getFirstValue(rating.created_at, rating.createdAt, rating.data_criacao),
    ),
    comments: getFirstValue(rating.comment, rating.review, '-'),
    deliveredOnTime,
    wouldBuyAgain,
    itemRatings: items,
  };
};

const extractRatingDetailsPayload = responseData => {
  if (!responseData) {
    return {};
  }

  if (responseData.data && typeof responseData.data === 'object') {
    return responseData.data;
  }

  if (responseData.result && typeof responseData.result === 'object') {
    return responseData.result;
  }

  if (responseData.rating && typeof responseData.rating === 'object') {
    return responseData.rating;
  }

  return responseData;
};

export default function EditRating() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();
  const history = useHistory();
  const { addToast } = useToasts();

  const { id } = useParams();
  const classes = useStyles();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get(`/ratings/order-ratings/${id}`);
      const ratingData = extractRatingDetailsPayload(response.data);
      const normalizedData = normalizeRatingDetails(ratingData || {});

      setData(normalizedData);
    } catch (e) {
      addToast(
        e.response?.data?.message
          ? e.response.data.message
          : 'Ocorreu um erro !',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );

      history.push('/admin/ratings');
    } finally {
      setLoading(false);
    }
  }, [id, addToast, history]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const renderBooleanChip = (label, value) => {
    if (value === true) {
      return (
        <Chip
          label={`${label}: Sim`}
          size="small"
          style={{ backgroundColor: '#2e7d32', color: '#fff' }}
        />
      );
    }

    if (value === false) {
      return (
        <Chip
          label={`${label}: Nao`}
          size="small"
          style={{ backgroundColor: '#c62828', color: '#fff' }}
        />
      );
    }

    return <Chip label={`${label}: -`} size="small" />;
  };

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>Dados da avaliação</h4>
            </CardHeader>
            <CardBody>
              {loading ? (
                <Box mt={6}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="name"
                        label="Nome"
                        name="name"
                        disabled
                        value={data.customerName}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        disabled
                        value={data.customerEmail}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={4} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone1"
                        label="Telefone 1"
                        name="telefone"
                        disabled
                        value={data.customerPhone1}
                        autoFocus
                      />
                    </GridItem>
                    <GridItem xs={12} sm={4} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone1"
                        label="Telefone 2"
                        name="telefone"
                        disabled
                        value={data.customerPhone2}
                        autoFocus
                      />
                    </GridItem>
                    <GridItem xs={12} sm={4} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone1"
                        label="Telefone 3"
                        name="telefone"
                        disabled
                        value={data.customerPhone3}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={8} md={8}>
                      <div
                        style={{
                          marginTop: 16,
                          border: '1px solid #ececec',
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ marginBottom: 6 }}
                        >
                          Nota da entrega (1 a 5)
                        </Typography>
                        {renderStars(data.deliveryScore)}
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={4} md={4}>
                      <div
                        style={{
                          marginTop: 16,
                          border: '1px solid #ececec',
                          borderRadius: 8,
                          padding: 12,
                          minHeight: 78,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {renderNps(data.npsScore)}
                      </div>
                      <Typography
                        variant="caption"
                        style={{
                          display: 'block',
                          marginTop: 6,
                          color: '#5f6368',
                        }}
                      >
                        NPS = o quanto o cliente recomenda a loja (0 a 10)
                      </Typography>
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="nopedido"
                        label="No pedido"
                        name="nopedido"
                        disabled
                        value={data.orderId}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="cadastrado"
                        label="Cadastrado em"
                        name="cadastrado"
                        disabled
                        value={data.createdAt}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={6} md={6}>
                      <div style={{ marginTop: 12 }}>
                        {renderBooleanChip(
                          'Entregue no prazo',
                          data.deliveredOnTime,
                        )}
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={6} md={6}>
                      <div style={{ marginTop: 12 }}>
                        {renderBooleanChip(
                          'Voltaria a comprar na loja',
                          data.wouldBuyAgain,
                        )}
                      </div>
                    </GridItem>
                  </GridContainer>

                  <br />
                  <hr />

                  <h4>Comentário geral da avaliação:</h4>

                  <p>{data.comments}</p>

                  <br />
                  <hr />

                  <h4 style={{ marginBottom: 8 }}>Itens avaliados</h4>
                  <Typography variant="caption" style={{ color: '#5f6368' }}>
                    {data.itemRatings.length} item(ns) avaliado(s)
                  </Typography>

                  {data.itemRatings.length > 0 ? (
                    <GridContainer style={{ marginTop: 8 }}>
                      {data.itemRatings.map(item => (
                        <GridItem xs={12} sm={12} md={12} key={item.key}>
                          <div
                            style={{
                              border: '1px solid #e6e9ef',
                              borderRadius: 10,
                              padding: 14,
                              marginBottom: 10,
                              backgroundColor: '#fafbff',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 8,
                                flexWrap: 'wrap',
                                marginBottom: 8,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 700 }}
                              >
                                {item.productName}
                              </Typography>
                              <Chip
                                label={`COD: ${item.productCode}`}
                                size="small"
                                style={{ backgroundColor: '#eef2ff' }}
                              />
                            </div>

                            <Typography
                              variant="caption"
                              style={{ display: 'block', color: '#5f6368' }}
                            >
                              Nota do produto
                            </Typography>
                            <div style={{ marginTop: 2 }}>
                              {renderStars(item.productScore)}
                            </div>

                            <div
                              style={{
                                marginTop: 10,
                                padding: 10,
                                borderRadius: 8,
                                backgroundColor: '#ffffff',
                                border: '1px solid #eceff5',
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{ display: 'block', color: '#5f6368' }}
                              >
                                Comentário do produto
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ marginTop: 4 }}
                              >
                                {item.comment}
                              </Typography>
                            </div>
                          </div>
                        </GridItem>
                      ))}
                    </GridContainer>
                  ) : (
                    <Box
                      style={{
                        marginTop: 8,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: '#f6f7fb',
                        color: '#5f6368',
                      }}
                    >
                      Nenhum item avaliado encontrado.
                    </Box>
                  )}
                </>
              )}
            </CardBody>
            <CardFooter>
              <Link to="/admin/ratings">
                <Button color="primary" type="button">
                  Voltar
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
